import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Users, Search, Plus, Trash2, Shield, ChevronRight, Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABEL = {
  admin: 'Administrateur',
  employee: 'Commercial',
  employer: 'Commercial',
  technicien: 'Technicien',
  investisseur: 'Investisseur',
  associe: 'Investisseur',
};
const ROLE_COLORS = {
  admin:         'bg-indigo-100 text-indigo-700 border-indigo-200',
  employee:      'bg-blue-100 text-blue-700 border-blue-200',
  employer:      'bg-blue-100 text-blue-700 border-blue-200',
  technicien:    'bg-emerald-100 text-emerald-700 border-emerald-200',
  investisseur:  'bg-amber-100 text-amber-700 border-amber-200',
  associe:       'bg-amber-100 text-amber-700 border-amber-200',
};

const initials = (name = '') =>
  name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');

const paletteColors = ['bg-gray-700'];


export default function Utilisateurs({ user, onLogout }) {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'employee' });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const load = async () => {
    try {
      const res = await axios.get(`${API}/api/profiles/list`);
      setProfiles(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    load();
    // eslint-disable-next-line
  }, []);

  const filtered = profiles.filter((p) => {
    const q = search.toLowerCase();
    return !q ||
      (p.name || '').toLowerCase().includes(q) ||
      (p.email || '').toLowerCase().includes(q) ||
      (p.role || '').toLowerCase().includes(q);
  });

  const handleCreate = async () => {
    if (!newUser.name.trim() || !newUser.email.trim() || !newUser.password.trim()) {
      toast.error('Nom, email et mot de passe requis');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/users`, newUser);
      toast.success('Utilisateur créé — email envoyé');
      setShowAdd(false);
      setNewUser({ name: '', email: '', password: '', role: 'employee' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création utilisateur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await axios.delete(`${API}/api/admin/users/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name || deleteTarget.email} supprimé`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression');
    }
  };

  // Accès réservé admin
  if (!isAdmin) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="utilisateurs">
        <div className="p-10 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mb-4">
            <Lock className="h-8 w-8 text-rose-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Accès réservé</h1>
          <p className="text-slate-500 mt-2 max-w-md">Ce module est accessible uniquement aux administrateurs.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="utilisateurs">
      <div className="p-6 space-y-5" data-testid="utilisateurs-page">
        {/* Header + Ajouter */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900 flex items-center gap-3">
              <Users className="h-7 w-7 text-blue-600" />
              Utilisateurs
            </h1>
            <p className="text-sm text-slate-500 mt-1">Gérez les comptes commerciaux, techniciens et administrateurs.</p>
          </div>
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            onClick={() => setShowAdd(true)}
            data-testid="btn-add-user"
          >
            <Plus className="h-4 w-4" />
            Ajouter un utilisateur
          </Button>
        </div>

        {/* Recherche */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un utilisateur par nom, email ou rôle…"
                className="pl-10"
                data-testid="input-search-users"
              />
            </div>
          </CardContent>
        </Card>

        {/* Tableau */}
        <Card>
          <CardContent className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200">
                    <th className="py-3 px-3 w-12"></th>
                    <th className="py-3 px-3">Nom</th>
                    <th className="py-3 px-3">Email</th>
                    <th className="py-3 px-3">Rôle</th>
                    <th className="py-3 px-3 text-right">CA (ce mois)</th>
                    <th className="py-3 px-3 text-right">Activités</th>
                    <th className="py-3 px-3 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={7} className="py-10 text-center text-slate-400">Chargement…</td></tr>
                  )}
                  {!loading && filtered.length === 0 && (
                    <tr><td colSpan={7} className="py-10 text-center text-slate-400">Aucun utilisateur</td></tr>
                  )}
                  {!loading && filtered.map((p, idx) => (
                    <tr
                      key={p.id || p.email}
                      className="border-b border-slate-100 hover:bg-blue-50/40 transition"
                      data-testid={`user-row-${p.email}`}
                    >
                      {/* Bouton supprimer en tête de ligne */}
                      <td className="py-3 px-3">
                        <button
                          onClick={() => setDeleteTarget(p)}
                          disabled={p.email === user?.email}
                          className={`p-1.5 rounded-md transition ${p.email === user?.email ? 'text-slate-300 cursor-not-allowed' : 'text-rose-500 hover:bg-rose-50 hover:text-rose-700'}`}
                          title={p.email === user?.email ? 'Impossible de supprimer son propre compte' : 'Supprimer'}
                          data-testid={`btn-delete-${p.email}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-full ${paletteColors[idx % paletteColors.length]} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                            {initials(p.name || p.email)}
                          </div>
                          <button
                            className="font-bold text-slate-900 hover:text-blue-600 text-left"
                            onClick={() => navigate(`/profil/${encodeURIComponent(p.email)}`)}
                          >
                            {p.name || p.email}
                          </button>
                        </div>
                      </td>
                      <td className="py-3 px-3 text-slate-600">{p.email}</td>
                      <td className="py-3 px-3">
                        <Badge className={`${ROLE_COLORS[p.role] || 'bg-slate-100 text-slate-700 border-slate-200'} font-semibold`}>
                          {p.role === 'admin' && <Shield className="h-3 w-3 mr-1 inline" />}
                          {ROLE_LABEL[p.role] || p.role}
                        </Badge>
                      </td>
                      <td className="py-3 px-3 text-right font-bold text-emerald-600">
                        {(p.ca_month || 0).toLocaleString('fr-FR')} €
                      </td>
                      <td className="py-3 px-3 text-right font-bold text-amber-600">
                        {p.activities_month || 0}
                      </td>
                      <td className="py-3 px-3">
                        <button
                          onClick={() => navigate(`/profil/${encodeURIComponent(p.email)}`)}
                          className="text-slate-400 hover:text-blue-600"
                          title="Voir le profil"
                          data-testid={`btn-view-${p.email}`}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dialog Ajouter un utilisateur */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md" data-testid="dialog-add-user">
          <div className="p-1">
            <h2 className="text-lg font-bold text-slate-900 mb-1">Ajouter un utilisateur</h2>
            <p className="text-xs text-slate-500 mb-4">Le compte sera créé et un email d'activation sera envoyé.</p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Nom complet *</Label>
                <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} placeholder="Jean Dupont" data-testid="newuser-name" />
              </div>
              <div>
                <Label className="text-xs">Email *</Label>
                <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="jean.dupont@sesam.com" data-testid="newuser-email" />
              </div>
              <div>
                <Label className="text-xs">Mot de passe *</Label>
                <Input type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="8 caractères minimum" data-testid="newuser-password" />
              </div>
              <div>
                <Label className="text-xs">Rôle</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                  <SelectTrigger data-testid="newuser-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrateur</SelectItem>
                    <SelectItem value="employee">Commercial</SelectItem>
                    <SelectItem value="technicien">Technicien</SelectItem>
                    <SelectItem value="investisseur">Investisseur</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setShowAdd(false)} disabled={saving}>Annuler</Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleCreate} disabled={saving} data-testid="btn-save-new-user">
                {saving ? 'Création…' : 'Créer l\'utilisateur'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation suppression */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="max-w-sm" data-testid="dialog-confirm-delete">
          <div className="p-1">
            <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center mb-3">
              <Trash2 className="h-6 w-6 text-rose-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">Supprimer l'utilisateur</h2>
            <p className="text-sm text-slate-600 mt-2">
              Confirmez la suppression de <b>{deleteTarget?.name || deleteTarget?.email}</b> ? Cette action est irréversible.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Annuler</Button>
              <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={handleDelete} data-testid="btn-confirm-delete">
                Supprimer définitivement
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
